import { UPLOAD_FAIL_MESSAGE } from '@/features/messages/queries/use-attachment-upload';
import imageCompression from 'browser-image-compression';
import dayjs from 'dayjs';
import { http } from '../api/http';
import { uuidv4 } from './uuid';

const getMinioLink = async (filePath: string, bucketName?: string): Promise<string> => {
  bucketName = bucketName || import.meta.env.VITE_MINIO_BUCKET;
  const response = await http.post<string>(`/files/upload`, {
    bucketName,
    objectName: filePath,
    expiryMinutes: 10
  });
  return response.data;
};

export const uploadAsync = async (file: File): Promise<string> => {
  const now = dayjs();
  const extension = file.name.substring(file.name.lastIndexOf('.') + 1);
  const path = `${now.format('YYYY')}/${now.format('MM')}/${now.format('DD')}/${uuidv4()}.${extension}`;
  const uploadUrl = await getMinioLink(path);
  const fileBytes = await file.arrayBuffer();

  if (file.type.includes('image/')) {
    const options = {
      maxSizeMB: 0.5, // Reduce from 1MB to 500KB for SD quality
      maxWidthOrHeight: 720, // SD resolution (720p)
      useWebWorker: true,
      initialQuality: 0.7, // Reduce quality for smaller file size
      fileType: 'image/jpeg' // Force JPEG for better compression
    };
    const compressedFile = await imageCompression(file, options);
    const compressedFileBytes = await compressedFile.arrayBuffer();
    const compressedResponse = await http.put(uploadUrl, compressedFileBytes);

    if (compressedResponse.status !== 200) throw new Error(UPLOAD_FAIL_MESSAGE);
    return path;
  }
  const response = await http.put(uploadUrl, fileBytes);
  if (response.status !== 200) throw new Error(UPLOAD_FAIL_MESSAGE);
  return path;
};

export const getFileUrl = async (path: string, bucketName?: string): Promise<string> => {
  bucketName = bucketName || import.meta.env.VITE_MINIO_BUCKET;
  const res = await http.get(`/files/download/${bucketName}/${path}`, {
    responseType: 'blob'
  });
  const blob = res.data;
  return URL.createObjectURL(blob);
};

export const buildFile = (path?: string): string | undefined => {
  if (!path) return undefined;
  const bucketName = import.meta.env.VITE_MINIO_BUCKET;
  return `/files/download/${bucketName}/${path}`;
};

export const downloadFile = (blobUrl: string, fileName: string) => {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
};

export const revokePreviewUrl = (url?: string) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

export const createImageLink = (path: Nullable<string>): string => {
  if (!path) return '';
  const url = `/${import.meta.env.VITE_MINIO_BUCKET}/${path}`;
  return url;
};

export const getMediaDuration = (file: File): Promise<number> => {
  if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
    return Promise.resolve(0);
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const media = new Audio(url);

    media.onloadedmetadata = () => {
      const durationMs = media.duration * 1000; // Convert to milliseconds
      URL.revokeObjectURL(url);
      resolve(Math.trunc(durationMs));
    };

    media.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
  });
};

export interface ThumbnailData {
  arrayBuffer: ArrayBuffer;
  width: number;
  height: number;
}

export const extractThumbnailFromVideo = (videoFile: File, timeInSeconds = 1): Promise<ThumbnailData> => {
  return new Promise((resolve, reject) => {
    if (!videoFile.type.startsWith('video/')) {
      resolve({
        arrayBuffer: new ArrayBuffer(0),
        width: 0,
        height: 0
      });
      return;
    }

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Canvas context not available'));
      return;
    }

    video.addEventListener('loadedmetadata', () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      video.currentTime = timeInSeconds;
    });

    video.addEventListener('seeked', async () => {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnailDataURL = canvas.toDataURL('image/jpeg', 0.8);
      const response = await fetch(thumbnailDataURL);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      URL.revokeObjectURL(video.src);
      resolve({
        arrayBuffer,
        width: canvas.width,
        height: canvas.height
      });
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Error loading video'));
    });

    video.src = URL.createObjectURL(videoFile);
  });
};

export const extractAndUploadThumbnail = async (
  videoFile: File
): Promise<{ url: string; width: number; height: number }> => {
  if (!videoFile.type.startsWith('video/')) return { url: '', width: 0, height: 0 };
  const { arrayBuffer, width, height } = await extractThumbnailFromVideo(videoFile);
  const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
  const file = new File([blob], `${videoFile.name}-thumbnail.jpg`, { type: 'image/jpeg' });
  return { url: await uploadAsync(file), width, height };
};
